import { defineStore } from 'pinia';
import { isUrl, storageLocal } from '@klondpay/utils';
import { isEqual } from 'lodash-unified';
import type { multiType, positionType } from '../types';

// 默认首页标签配置
let defaultHomeTag: multiType = {
  path: '/welcome',
  parentPath: '/',
  name: 'welcome',
  meta: {
    title: 'menus.hshome',
    icon: 'home-filled',
  },
};

/**
 * 设置默认首页标签
 */
export function setDefaultHomeTag(tag: multiType) {
  defaultHomeTag = tag;
}

export const useMultiTagsStore = defineStore({
  id: 'vue3-admin-multiTags',
  state: () => ({
    // 存储标签页信息（路由信息）
    multiTags: storageLocal.getItem('responsive-configure')?.multiTagsCache
      ? storageLocal.getItem('responsive-tags')
      : [defaultHomeTag],
    multiTagsCache: storageLocal.getItem('responsive-configure')?.multiTagsCache,
  }),
  getters: {
    getMultiTagsCache(): boolean {
      return this.multiTagsCache;
    },
  },
  actions: {
    multiTagsCacheChange(multiTagsCache: boolean) {
      this.multiTagsCache = multiTagsCache;
      if (multiTagsCache) {
        storageLocal.setItem('responsive-tags', this.multiTags);
      } else {
        storageLocal.removeItem('responsive-tags');
      }
    },
    tagsCache(multiTags: multiType[]) {
      if (this.getMultiTagsCache) {
        storageLocal.setItem('responsive-tags', multiTags);
      }
    },
    handleTags<T>(mode: string, value?: T | multiType, position?: positionType): T | undefined {
      switch (mode) {
        case 'equal':
          this.multiTags = value as any;
          this.tagsCache(this.multiTags);
          break;
        case 'push':
          {
            const tagVal = value as multiType;
            if (isUrl(tagVal?.name)) return;
            const tagPath = tagVal?.path;
            // 判断tag是否已存在
            const tagHasExits = this.multiTags.some((tag: multiType) => {
              return tag.path === tagPath;
            });

            // 判断tag中的query键值是否相等
            const tagQueryHasExits = this.multiTags.some((tag: multiType) => {
              return isEqual(tag.query, tagVal?.query);
            });

            if (tagHasExits && tagQueryHasExits) return;

            const dynamicLevel = tagVal?.meta?.dynamicLevel ?? -1;
            if (dynamicLevel > 0) {
              // dynamicLevel动态路由可打开的数量
              // 获取到已经打开的动态路由数, 判断是否大于dynamicLevel
              if (
                this.multiTags.filter((e: multiType) => e?.path === tagPath).length >= dynamicLevel
              ) {
                // 关闭第一个
                const index = this.multiTags.findIndex((item: multiType) => item?.path === tagPath);
                if (index !== -1) this.multiTags.splice(index, 1);
              }
            }
            this.multiTags.push(value);
            this.tagsCache(this.multiTags);
          }
          break;
        case 'splice':
          if (!position) {
            const index = this.multiTags.findIndex((v: multiType) => v.path === value);
            if (index === -1) return;
            this.multiTags.splice(index, 1);
          } else {
            this.multiTags.splice(position?.startIndex, position?.length);
          }
          this.tagsCache(this.multiTags);
          return this.multiTags as T;
        case 'slice':
          return this.multiTags.slice(-1) as T;
      }
    },
  },
});

// 导出快捷 hook
export const useMultiTagsStoreHook = () => useMultiTagsStore();

// 导出 hook 函数创建器
export function createMultiTagsStoreHook(store: any) {
  return function () {
    return useMultiTagsStore(store);
  };
}
